import { computed, inject, Injectable, signal } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { produce } from 'immer';
import { EMPTY, from, concatMap, mergeMap, switchMap } from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';
import { SmartbookHttpService, UserService } from 'shared/domain';

import { IndexedDbService } from './indexed-db.service';

type InternalSyncState = {
  synced: Set<string>;
  remaining: Set<string>;
  errors: Set<string>;
};

export type GuidelineSyncStatus = {
  syncing: boolean;
  synced: string[];
  remaining: string[];
  errors: string[];
};

@Injectable({ providedIn: 'root' })
export class GuidelineSyncService {
  private guidelinesHttpService = inject(SmartbookHttpService);
  private indexedDbService = inject(IndexedDbService);
  private userService = inject(UserService);

  private _state = signal<InternalSyncState>({
    synced: new Set(),
    remaining: new Set(),
    errors: new Set(),
  });

  public syncStatus = computed<GuidelineSyncStatus>(() => {
    const s = this._state();
    return {
      syncing: s.remaining.size > 0,
      synced: [...s.synced],
      remaining: [...s.remaining],
      errors: [...s.errors],
    };
  });

  progress = computed(() => {
    const s = this._state();
    const total = s.synced.size + s.remaining.size + s.errors.size;
    return total === 0 ? 0 : (s.synced.size / total) * 100;
  });

  public syncGuidelinesForCurrentUser = rxMethod<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() => this.userService.getGuidelinesToSync()),
      switchMap(async (guidelineSyncInfos) => {
        const ids = guidelineSyncInfos.map((g) => g.uniqueId);
        const local = await this.indexedDbService.getGuidelinesByUniqueId(ids);

        const localMap = new Map(local.map((g) => [g.uniqueId, g.version]));

        const outOfSync = guidelineSyncInfos.filter((dto) => !localMap.has(dto.uniqueId) || localMap.get(dto.uniqueId) !== dto.version);

        const alreadySynced = guidelineSyncInfos
          .filter((dto) => localMap.has(dto.uniqueId) && localMap.get(dto.uniqueId) === dto.version)
          .map((dto) => dto.uniqueId);

        this._state.set({
          synced: new Set(alreadySynced),
          remaining: new Set(outOfSync.map((g) => g.uniqueId)),
          errors: new Set(),
        });

        this.indexedDbService.putUserSyncInfo(this.userService.user().id, guidelineSyncInfos);

        if (outOfSync.length) {
          this._syncGuidelines(outOfSync.map((g) => g.uniqueId));
        }
      }),
    ),
  );

  private _syncGuidelines = rxMethod<string[]>((ids$) =>
    ids$.pipe(
      mergeMap((ids) => {
        if (!ids.length) {
          return EMPTY;
        }

        this._state.set(
          produce(this._state(), (draft) => {
            ids.forEach((id) => draft.remaining.add(id));
          }),
        );

        const chunkSize = 8;
        const chunks: string[][] = [];

        for (let i = 0; i < ids.length; i += chunkSize) {
          chunks.push(ids.slice(i, i + chunkSize));
        }

        const delayBetweenChunksMs = 500;

        return from(chunks).pipe(
          concatMap((chunk) =>
            this.guidelinesHttpService.getSmartbookWithSignatureCheck(chunk).pipe(
              tap(async (guidelines) => {
                await Promise.all(guidelines.map((signedGuideline) => this.indexedDbService.putGuidelineWithSignature(signedGuideline)));

                this._state.set(
                  produce(this._state(), (draft) => {
                    guidelines.forEach((signedGuideline) => {
                      draft.synced.add(signedGuideline.uniqueId);
                      draft.remaining.delete(signedGuideline.uniqueId);
                    });
                  }),
                );
              }),
              delay(delayBetweenChunksMs),
              catchError(() => {
                this._state.set(
                  produce(this._state(), (draft) => {
                    chunk.forEach((id) => {
                      draft.errors.add(id);
                      draft.remaining.delete(id);
                    });
                  }),
                );

                return EMPTY;
              }),
            ),
          ),
        );
      }),
    ),
  );

  public async init() {
    await this.indexedDbService.init();
  }
}
